import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'reactstrap';

import CheckboxInputField from 'components/layout/forms/CheckboxInputField';
import getDefaultDoorMetaData from 'components/configurator/services/getDefaultDoorMetaData';
import getPropertyMax from 'services/objects/getPropertyMax';
import glassWallPartTypes from 'components/configurator/types/glassWallPartTypes';
import IGlassWallDoor from 'components/interfaces/IGlassWallDoor';
import IGlassWallGlass from 'components/interfaces/IGlassWallGlass';
import NumberInputField from 'components/layout/forms/NumberInputField';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import translations from 'services/translations/translations';
import store from 'store/store';
import { setWall, setWallMetaData } from 'store/selectedWall/actions';

import {
  DOOR_GLASS_1276,
  DOOR_GLASS_3276,
  DOOR_GLASS_876,
} from 'components/configurator/types/doorGlassTypes';

import {
  is2kAluGlazy,
  is2kTypeWall,
  isSlidy30,
  isSlidy36,
  isWoodyTypeWall,
} from 'components/configurator/types/glazingWallTypesCheck';

import DoorSelectRow from './DoorSelectRow';
import getDefaultGlassWallDoor from './getDefaultGlassWallDoor';
import handleGlassWallProperties from '../handleGlassWallProperties';
import SelectedDoorProperties from './SelectedDoorProperties';
import updateDependenciesBasedOnFirstDoorLastDoor from '../updateDependenciesBasedOnFirstDoorLastDoor';
import GlazyDoubleProperties from '../GlazyDoubleProperties';
import IValidationError from '../IValidationError';

import {
  doorSideOptions,
  verticalProfileOptions,
} from '../../../../types/doorOptions';

import 'style/configurator/Modal.css';
import { isAluTypeDoor, isWoodyTypeDoor } from '../../../../types/doorTypes';
import { HANDLE_TYPE_DEFAULT } from '../../../../types/handleTypes';

const DoorProperties = ({
  doors,
  setChangedFirstLastDoor,
  changedFirstLastDoor,
  setChangedFirstLastDoorInWall,
  disableInputs,
  validationErrors,
}: {
  doors: IGlassWallDoor[];
  setChangedFirstLastDoor: (changed: boolean) => void;
  changedFirstLastDoor: boolean;
  setChangedFirstLastDoorInWall: (changed: boolean) => void;
  disableInputs?: boolean;
  validationErrors: IValidationError[];
}): JSX.Element | null => {
  const { t } = useTranslation([
    translations.glassWallDoorProperties,
    translations.glassWallProperties,
  ]);

  const dispatch = useDispatch();
  const wall = useSelector((state: any) => state.selectedWall.properties);
  const wallMetaData = useSelector((state: any) => state.selectedWall.metaData);
  const [selectedDoor, setSelectedDoor] = useState<number>(0);

  const getUpdatedWall = () => store.getState().selectedWall.properties;

  const addDoor = () => {
    const doorId = getPropertyMax('id')(doors) + 1;
    const newDoor = getDefaultGlassWallDoor(doorId, wall);

    newDoor.doorSide = wall.doorSide;

    if (isSlidy30(wall)) {
      newDoor.doorGlassType = DOOR_GLASS_876; // preset value: 4+076+4mm
    }

    if (isSlidy36(wall)) {
      newDoor.doorGlassType = DOOR_GLASS_1276; // preset value: 6+6 075 SC
    }

    if (is2kTypeWall(wall)) {
      newDoor.doorGlassType = DOOR_GLASS_3276;
    }

    if (isWoodyTypeDoor(newDoor)) {
      newDoor.handleType = HANDLE_TYPE_DEFAULT;
      newDoor.doorOpeningWidth = 990;
      newDoor.doorOpeningHeight = 2090;
    }

    if (isAluTypeDoor(newDoor)) {
      newDoor.doorOpeningWidth = 1000;
    }

    if (isWoodyTypeWall(wall)) {
      wall.woodyDoorFrameColor = 'RAL 9003';
      wall.woodyDoorLeafColor = 'RAL 9003';
    }

    const onlyDoor = doors.length === 0 ? wall.onlyDoor : false;

    dispatch(
      setWallMetaData({
        ...wallMetaData,
        doors: [
          ...wallMetaData.doors,
          {
            ...getDefaultDoorMetaData(),
            doorTempId: doorId,
            doorId: newDoor.doorID,
          },
        ],
      })
    );

    dispatch(
      setWall({
        ...wall,
        parts: [
          ...wall.parts.map((part: IGlassWallDoor | IGlassWallGlass) => {
            if (part.type === glassWallPartTypes.glass) {
              return part;
            }

            const door = { ...part } as IGlassWallDoor;
            door.isLastDoor = false;
            return door;
          }),
          newDoor,
        ],
        onlyDoor,
      })
    );

    setSelectedDoor(doors.length);
  };

  const onDeleteDoor = () => {
    setSelectedDoor(0);
    dispatch(
      setWall({
        ...wall,
        onlyDoor: false,
        parts: [
          wall.parts.filter(
            (part: any, index: number) => index !== selectedDoor
          ),
        ],
      })
    );
  };

  const getAddDoorButton = (size?: string) => (
    <PrimaryButton className="modal-add-button" onClick={addDoor} size={size}>
      Add door
    </PrimaryButton>
  );

  useEffect(() => {
    if (changedFirstLastDoor) {
      updateDependenciesBasedOnFirstDoorLastDoor(getUpdatedWall());
      setChangedFirstLastDoor(false);
    }
  }, [changedFirstLastDoor]);

  if (selectedDoor !== 0 && doors[selectedDoor] === undefined) {
    return null;
  }

  return (
    <>
      <Row>
        <Col>
          <SelectInputFieldControlled
            id="doorSide"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Door Side`
            )}
            onChange={handleGlassWallProperties}
            name="doorSide"
            required
            options={doorSideOptions()}
            value={wall.doorSide}
            disabled={disableInputs}
          />
        </Col>
        <Col className={isSlidy30(wall) || isSlidy36(wall) ? 'hidden' : ''}>
          <NumberInputField
            id="doorHeight"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Door Height`
            )}
            onChange={handleGlassWallProperties}
            value={wall.doorHeight}
            name="doorHeight"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        {wall.glazingWallType.includes('woody') ||
        wall.glazingWallType.includes('alu') ? (
          <Col>
            <NumberInputField
              id="AluDoorRoofOffset"
              label={t(
                `${translations.glassWallDoorProperties}:fields:All door roof offset`
              )}
              onChange={handleGlassWallProperties}
              value={wall.AluDoorRoofOffset}
              name="AluDoorRoofOffset"
              required
              disabled={disableInputs}
            />
          </Col>
        ) : null}
        <Col>
          <SelectInputFieldControlled
            id="leftVerticalProfileType"
            label={t(
              `${translations.glassWallProperties}:fields:Left Vertical Profile`
            )}
            name="leftVerticalProfileType"
            onChange={handleGlassWallProperties}
            options={verticalProfileOptions('left', getUpdatedWall())}
            required
            value={wall.leftVerticalProfileType}
            disabled={disableInputs || is2kAluGlazy(wall)}
          />
        </Col>
        <Col>
          <SelectInputFieldControlled
            id="rightVerticalProfileType"
            label={t(
              `${translations.glassWallProperties}:fields:Right Vertical Profile`
            )}
            name="rightVerticalProfileType"
            onChange={handleGlassWallProperties}
            options={verticalProfileOptions('right', getUpdatedWall())}
            required
            value={wall.rightVerticalProfileType}
            disabled={disableInputs || is2kAluGlazy(wall)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <GlazyDoubleProperties wall={wall} disableInputs={disableInputs} />
        </Col>
      </Row>
      <Row>
        <Col>
          <CheckboxInputField
            id="showOpenDoors"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Show Open Doors`
            )}
            onChange={handleGlassWallProperties}
            value={wall.showOpenDoors}
            name="showOpenDoors"
            required
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="floorProfileWithColor"
            label={t(
              `${translations.glassWallDoorProperties}:fields:Floor Profile With Color`
            )}
            onChange={handleGlassWallProperties}
            value={wall.floorProfileWithColor}
            name="floorProfileWithColor"
            required
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <CheckboxInputField
            id="hasLock"
            label={t(`${translations.glassWallDoorProperties}:fields:Has lock`)}
            onChange={handleGlassWallProperties}
            value={wall.hasLock}
            name="hasLock"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      {doors.length === 0 ? (
        getAddDoorButton()
      ) : (
        <>
          <hr />
          {!disableInputs ? (
            <DoorSelectRow
              doors={doors}
              getAddDoorButton={getAddDoorButton}
              onDeleteDoor={onDeleteDoor}
              selectedDoor={selectedDoor}
              setSelectedDoor={setSelectedDoor}
            />
          ) : null}
          <SelectedDoorProperties
            doors={doors}
            selectedDoor={selectedDoor}
            setChangedFirstLastDoor={setChangedFirstLastDoor}
            setChangedFirstLastDoorInWall={setChangedFirstLastDoorInWall}
            disableInputs={disableInputs}
            validationErrors={validationErrors}
          />
        </>
      )}
    </>
  );
};

export default DoorProperties;
